import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CookiesPolicy = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('es');

  useEffect(() => {
    const storedLang = localStorage.getItem('autogoLanguage') || 'es';
    setLanguage(storedLang);
  }, []);

  const content = {
    es: {
      backButton: 'Volver al inicio',
      title: 'Política de Cookies',
      note: '',
    },
    en: {
      backButton: 'Back to home',
      title: 'Cookies Policy',
      note: 'Note: This legal document is available in Spanish only.',
    },
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#0A4F6E] hover:text-[#39FF77] mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          {t.backButton}
        </button>

        <h1 className="text-4xl font-bold text-[#0A4F6E] mb-8">{t.title}</h1>

        {language === 'en' && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
            <p className="text-sm text-blue-700">{t.note}</p>
          </div>
        )}

        <div className="prose prose-lg max-w-none">
          <p className="mb-6 text-lg">
            En AutoGo Delivery utilizamos cookies para mejorar tu experiencia de navegación, mostrarte anuncios o contenidos personalizados y analizar nuestro tráfico.
          </p>

          <h2 className="text-2xl font-bold text-[#0A4F6E] mt-8 mb-4">¿Qué son las cookies?</h2>
          <p className="mb-4">
            Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo (ordenador, tablet o móvil) cuando visitas nuestra página web. Estas cookies permiten que el sitio web recuerde tus acciones y preferencias durante un período de tiempo, para que no tengas que volver a configurarlas cada vez que regreses al sitio o navegues de una página a otra.
          </p>

          <h2 className="text-2xl font-bold text-[#0A4F6E] mt-8 mb-4">Tipos de cookies que utilizamos</h2>

          <h3 className="text-xl font-bold text-[#0A4F6E] mt-6 mb-3">Cookies necesarias</h3>
          <p className="mb-4">
            Estas cookies son esenciales para el funcionamiento del sitio web. Permiten funciones básicas como la navegación por páginas y el acceso a áreas seguras del sitio web. El sitio web no puede funcionar correctamente sin estas cookies.
          </p>

          <h3 className="text-xl font-bold text-[#0A4F6E] mt-6 mb-3">Cookies funcionales</h3>
          <p className="mb-4">
            Estas cookies permiten que el sitio web recuerde las opciones que has elegido (como tu nombre de usuario, idioma o región) y proporcionan características mejoradas y más personales. También pueden utilizarse para recordar los cambios que has realizado en el tamaño del texto, fuentes y otras partes de las páginas web que puedes personalizar.
          </p>

          <h3 className="text-xl font-bold text-[#0A4F6E] mt-6 mb-3">Cookies analíticas</h3>
          <p className="mb-4">
            Estas cookies nos ayudan a comprender cómo los visitantes interactúan con nuestro sitio web, recopilando y proporcionando información de forma anónima. Nos permiten mejorar el funcionamiento del sitio web.
          </p>

          <h3 className="text-xl font-bold text-[#0A4F6E] mt-6 mb-3">Cookies de rendimiento</h3>
          <p className="mb-4">
            Estas cookies se utilizan para analizar y comprender los índices de rendimiento clave del sitio web, lo que nos ayuda a proporcionar una mejor experiencia de usuario para los visitantes.
          </p>

          <h3 className="text-xl font-bold text-[#0A4F6E] mt-6 mb-3">Cookies publicitarias</h3>
          <p className="mb-4">
            Estas cookies se utilizan para proporcionar a los visitantes anuncios personalizados basados en las páginas que visitaste anteriormente y para analizar la efectividad de las campañas publicitarias.
          </p>

          <h2 className="text-2xl font-bold text-[#0A4F6E] mt-8 mb-4">Gestión de cookies</h2>
          <p className="mb-4">
            Puedes controlar y/o eliminar las cookies como desees. Puedes eliminar todas las cookies que ya están en tu dispositivo y puedes configurar la mayoría de los navegadores para evitar que se instalen. Sin embargo, si haces esto, es posible que tengas que ajustar manualmente algunas preferencias cada vez que visites un sitio y que algunos servicios y funcionalidades no funcionen.
          </p>

          <h3 className="text-xl font-bold text-[#0A4F6E] mt-6 mb-3">Cómo deshabilitar las cookies en tu navegador</h3>
          <ul className="list-disc list-inside mb-4 space-y-2">
            <li>
              <strong>Chrome:</strong> Configuración → Privacidad y seguridad → Cookies y otros datos de sitios
            </li>
            <li>
              <strong>Firefox:</strong> Opciones → Privacidad y seguridad → Cookies y datos del sitio
            </li>
            <li>
              <strong>Safari:</strong> Preferencias → Privacidad → Cookies y datos de sitios web
            </li>
            <li>
              <strong>Edge:</strong> Configuración → Cookies y permisos del sitio → Cookies y datos del sitio
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#0A4F6E] mt-8 mb-4">Consentimiento</h2>
          <p className="mb-4">
            Al utilizar nuestro sitio web, aceptas el uso de cookies de acuerdo con esta Política de Cookies. Si no estás de acuerdo con nuestro uso de cookies, debes configurar tu navegador en consecuencia o no utilizar nuestro sitio web.
          </p>

          <h2 className="text-2xl font-bold text-[#0A4F6E] mt-8 mb-4">Más información</h2>
          <p className="mb-4">Para cualquier consulta relacionada con nuestra Política de Cookies, puedes contactarnos en:</p>
          <div className="bg-gray-50 p-6 rounded-lg mb-4">
            <p className="mb-2">
              <strong>AUTOGO – PYRAMID LOGISTICS SL</strong>
            </p>
            <p className="mb-2">Domicilio: Av Olof Palme 6, 08840, Viladecans, Barcelona</p>
            <p className="mb-2">
              Correo electrónico:{' '}
              <a href="mailto:hello@autogodelivery.com" className="text-[#39FF77] hover:underline">
                hello@autogodelivery.com
              </a>
            </p>
            <p>CIF B72448764</p>
          </div>

          <p className="text-sm text-gray-600 mt-8">Última actualización: Noviembre 2025</p>
        </div>
      </div>
    </div>
  );
};

export default CookiesPolicy;
